import React, { useState, useEffect, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Plus, Minus, Maximize, Factory, Fuel, Layers3 } from 'lucide-react';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  Line,
  ZoomableGroup
} from 'react-simple-maps';

const geoUrl = "/js/countries-50m.json";

type MapLocation = {
  id: string;
  title: string;
  subtitle: string;
  type: 'division' | 'refineria' | 'petroquimico' | 'faja';
  coordinates: [number, number];
  featured?: boolean;
};

const ORIGIN: [number, number] = [-64.2454, 8.8875]; // Sede Principal

const LOCATIONS: MapLocation[] = [
  // Ubicaciones operacionales
  {
    id: 'occidente',
    title: 'División Occidente',
    subtitle: 'Maracaibo, Zulia',
    type: 'division',
    coordinates: [-71.6125, 10.6427],
  },
  {
    id: 'sede',
    title: 'Sede Principal',
    subtitle: 'El Tigre, Anzoátegui',
    type: 'division',
    coordinates: [-64.2454, 8.8875],
    featured: true,
  },
  {
    id: 'faja-division',
    title: 'División Faja',
    subtitle: 'Faja Petrolífera del Orinoco',
    type: 'division',
    coordinates: [-63.5, 8.5],
  },
  {
    id: 'norte',
    title: 'División Norte',
    subtitle: 'Oriente y Monagas',
    type: 'division',
    coordinates: [-64.6, 10.2],
  },

  // Refinerías
  {
    id: 'amuay',
    title: 'Refinería de Amuay',
    subtitle: 'Los Taques, Falcón',
    type: 'refineria',
    coordinates: [-70.20917, 11.74083],
  },
  {
    id: 'cardon',
    title: 'Refinería Cardón',
    subtitle: 'Punta Cardón, Falcón',
    type: 'refineria',
    coordinates: [-70.22083, 11.63389],
  },
  {
    id: 'el-palito',
    title: 'Refinería El Palito',
    subtitle: 'Puerto Cabello, Carabobo',
    type: 'refineria',
    coordinates: [-68.123, 10.481],
  },
  {
    id: 'puerto-la-cruz',
    title: 'Refinería Puerto La Cruz',
    subtitle: 'Puerto La Cruz, Anzoátegui',
    type: 'refineria',
    coordinates: [-64.621, 10.221],
  },

  // Petroquímica
  {
    id: 'jose-anzoategui',
    title: 'Complejo Petroquímico José Antonio Anzoátegui',
    subtitle: 'José, Anzoátegui',
    type: 'petroquimico',
    coordinates: [-64.86438, 10.06922],
  },
  {
    id: 'ana-maria-campos',
    title: 'Complejo Petroquímico Ana María Campos',
    subtitle: 'El Tablazo, Zulia',
    type: 'petroquimico',
    coordinates: [-71.47, 10.98],
  },
  {
    id: 'hugo-chavez-moron',
    title: 'Complejo Petroquímico Hugo Chávez',
    subtitle: 'Morón, Carabobo',
    type: 'petroquimico',
    coordinates: [-68.2, 10.49],
  },

  // Faja Petrolífera del Orinoco - Nodos de referencia visual
  {
    id: 'boyaca',
    title: 'Campo Boyacá',
    subtitle: 'Centro-sur de Guárico',
    type: 'faja',
    coordinates: [-66.4, 8.35],
  },
  {
    id: 'junin',
    title: 'Campo Junín',
    subtitle: 'Sur de Anzoátegui / SE de Guárico',
    type: 'faja',
    coordinates: [-64.95, 8.05],
  },
  {
    id: 'ayacucho',
    title: 'Campo Ayacucho',
    subtitle: 'Sur de Anzoátegui',
    type: 'faja',
    coordinates: [-63.45, 8.1],
  },
  {
    id: 'carabobo-fpo',
    title: 'Campo Carabobo',
    subtitle: 'Sur de Monagas / SE de Anzoátegui',
    type: 'faja',
    coordinates: [-62.55, 8.55],
  },

  // Empresas/bloques emblemáticos de la Faja
  {
    id: 'petromonagas',
    title: 'Petromonagas',
    subtitle: 'Área Carabobo / José',
    type: 'faja',
    coordinates: [-62.95, 8.7],
  },
  {
    id: 'petrocedeno',
    title: 'Petrocedeño',
    subtitle: 'Área Junín / José',
    type: 'faja',
    coordinates: [-63.95, 8.55],
  },
  {
    id: 'petropiar',
    title: 'Petropiar',
    subtitle: 'Área Junín / José',
    type: 'faja',
    coordinates: [-64.25, 8.3],
  },
  {
    id: 'sinovensa',
    title: 'Sinovensa',
    subtitle: 'Área Carabobo',
    type: 'faja',
    coordinates: [-62.7, 8.9],
  },
  {
    id: 'petroanzoategui',
    title: 'Petroanzoátegui',
    subtitle: 'Área Junín',
    type: 'faja',
    coordinates: [-64.45, 8.6],
  },
];

const TYPE_META = {
  division: {
    color: '#007AFF',
    glow: 'rgba(0,122,255,0.45)',
    label: 'División',
  },
  refineria: {
    color: '#FF6B00',
    glow: 'rgba(255,107,0,0.45)',
    label: 'Refinería',
  },
  petroquimico: {
    color: '#22C55E',
    glow: 'rgba(34,197,94,0.45)',
    label: 'Complejo petroquímico',
  },
  faja: {
    color: '#A855F7',
    glow: 'rgba(168,85,247,0.45)',
    label: 'Faja del Orinoco',
  },
};

const getInitialPosition = (isMobile: boolean) => ({
  coordinates: isMobile ? [-66.3, 8.2] as [number, number] : [-66.3, 6.6] as [number, number],
  zoom: 1.0,
});

const getMarkerSize = (loc: MapLocation) => {
  if (loc.featured) return 1.3;
  if (loc.type === 'faja') return 0.88;
  return 1;
};

const renderMarkerShape = (loc: MapLocation, isActive: boolean) => {
  const meta = TYPE_META[loc.type];
  const scale = getMarkerSize(loc);

  if (loc.featured) {
    return (
      <g transform={`scale(${scale})`}>
        {isActive && (
          <motion.circle
            r="15"
            fill="none"
            stroke={meta.color}
            strokeWidth="2"
            initial={{ opacity: 0.8, scale: 1 }}
            animate={{ opacity: 0, scale: 2.8 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
          />
        )}
        <polygon
          points="0,-12 3,-4 11,-4 5,1 7,9 0,5 -7,9 -5,1 -11,-4 -3,-4"
          fill={meta.color}
          stroke="#fff"
          strokeWidth="1.2"
          style={{ filter: `drop-shadow(0 0 12px ${meta.glow})` }}
        />
      </g>
    );
  }

  if (loc.type === 'faja') {
    return (
      <g transform={`scale(${scale})`}>
        {isActive && (
          <motion.circle
            r="12"
            fill="none"
            stroke={meta.color}
            strokeWidth="2"
            initial={{ opacity: 0.8, scale: 1 }}
            animate={{ opacity: 0, scale: 2.4 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
          />
        )}
        <circle
          r="6"
          fill={meta.color}
          stroke="#fff"
          strokeWidth="1.2"
          style={{ filter: `drop-shadow(0 0 10px ${meta.glow})` }}
        />
        <circle
          r="12"
          fill="none"
          stroke={meta.color}
          strokeWidth="1"
          strokeDasharray="3 3"
          opacity="0.7"
        />
      </g>
    );
  }

  return (
    <g transform={`translate(-12, -24) scale(${scale})`}>
      {isActive && (
        <motion.circle
          cx="12"
          cy="12"
          r="12"
          fill="none"
          stroke={meta.color}
          strokeWidth="2"
          initial={{ opacity: 0.8, scale: 1 }}
          animate={{ opacity: 0, scale: 2.5 }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
        />
      )}
      <MapPin
        size={24}
        color={meta.color}
        fill="rgba(10, 25, 47, 0.92)"
        strokeWidth={1.7}
        style={{ filter: `drop-shadow(0 0 10px ${meta.glow})` }}
      />
    </g>
  );
};

function useLightMode() {
  const [isLightMode, setIsLightMode] = useState(() => 
    typeof document !== 'undefined' && document.documentElement.classList.contains('light-mode')
  );

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const observer = new MutationObserver(() => {
      setIsLightMode(document.documentElement.classList.contains('light-mode'));
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, []);

  return isLightMode;
}

export default function InteractiveMap() {
  const isLightMode = useLightMode();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoRotating, setIsAutoRotating] = useState(true);
  const [activeFilter, setActiveFilter] = useState<'all' | 'division' | 'refineria' | 'petroquimico' | 'faja'>('all');
  const [screenSize, setScreenSize] = useState({
    isMobile: window.innerWidth < 768,
    isTablet: window.innerWidth >= 768 && window.innerWidth < 1024
  });

  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        isMobile: window.innerWidth < 768,
        isTablet: window.innerWidth >= 768 && window.innerWidth < 1024
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { isMobile, isTablet } = screenSize;

  const [position, setPosition] = useState(() => getInitialPosition(isMobile));

  // Handle re-centering when screen size changes significantly
  useEffect(() => {
    setPosition(getInitialPosition(isMobile));
  }, [isMobile]);

  const filteredLocations = useMemo(() => {
    if (activeFilter === 'all') return LOCATIONS;
    return LOCATIONS.filter(l => l.type === activeFilter);
  }, [activeFilter]);

  const activeLocation = filteredLocations[activeIndex] || filteredLocations[0];

  const groupedStats = useMemo(() => {
    return {
      divisiones: LOCATIONS.filter(l => l.type === 'division').length,
      refinerias: LOCATIONS.filter(l => l.type === 'refineria').length,
      petroquimicos: LOCATIONS.filter(l => l.type === 'petroquimico').length,
      faja: LOCATIONS.filter(l => l.type === 'faja').length,
    };
  }, []);

  // Reset activeIndex when filter changes
  useEffect(() => {
    setActiveIndex(0);
  }, [activeFilter]);

  useEffect(() => {
    if (!isAutoRotating || filteredLocations.length <= 1) return;
    const interval = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % filteredLocations.length);
    }, 4200);
    return () => clearInterval(interval);
  }, [isAutoRotating, filteredLocations]);

  const handleZoomIn = () => {
    if (position.zoom >= 4) return;
    setPosition(pos => ({ ...pos, zoom: pos.zoom * 1.35 }));
  };

  const handleZoomOut = () => {
    if (position.zoom <= 1) return;
    setPosition(pos => ({ ...pos, zoom: pos.zoom / 1.35 }));
  };

  const handleReset = () => {
    setPosition(getInitialPosition(isMobile));
  };

  return (
    <div
      style={{
        width: '100%',
        height: isMobile ? '500px' : (isTablet ? '520px' : '580px'),
        position: 'relative',
        background: isLightMode ? '#ffffff' : '#04070F',
        borderRadius: '24px',
        overflow: 'hidden',
        border: isLightMode ? '1px solid rgba(0, 122, 255, 0.15)' : '1px solid rgba(0, 122, 255, 0.2)',
        boxShadow: isLightMode ? '0 20px 50px rgba(0, 122, 255, 0.05)' : '0 25px 60px rgba(0,0,0,0.6)',
      }}
    >
      {/* Background radial glow */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: isLightMode
            ? 'radial-gradient(circle at 50% 50%, rgba(0,122,255,0.08) 0%, rgba(0,122,255,0.02) 40%, rgba(0,0,0,0) 70%)'
            : 'radial-gradient(circle at 50% 50%, rgba(0,122,255,0.18) 0%, rgba(0,122,255,0.05) 30%, rgba(0,0,0,0) 70%)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* Pill tags displaying network stats (interactive filters) */}
      <div
        style={{
          position: 'absolute',
          left: isMobile ? '12px' : '20px',
          top: isMobile ? '12px' : '20px',
          right: isMobile ? '12px' : 'auto',
          zIndex: 45,
          display: isMobile ? 'grid' : 'flex',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'none',
          gap: isMobile ? '6px' : '10px',
          pointerEvents: 'auto',
        }}
      >
        {[
          { id: 'all', label: 'Ver Todos', color: '#007AFF', icon: <Maximize size={12} /> },
          { id: 'division', label: `${groupedStats.divisiones} divisiones`, color: TYPE_META.division.color, icon: <Layers3 size={14} /> },
          { id: 'refineria', label: `${groupedStats.refinerias} refinerías`, color: TYPE_META.refineria.color, icon: <Fuel size={14} /> },
          { id: 'petroquimico', label: `${groupedStats.petroquimicos} petroquímicos`, color: TYPE_META.petroquimico.color, icon: <Factory size={14} /> },
          { id: 'faja', label: `${groupedStats.faja} nodos FPO`, color: TYPE_META.faja.color, icon: <MapPin size={14} /> },
        ].map((item) => {
          const isCurrent = activeFilter === item.id;
          return (
            <button
              key={item.id}
              aria-label={item.label}
              onClick={() => {
                setIsAutoRotating(false);
                setActiveFilter(item.id as any);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 18px',
                borderRadius: '10px',
                color: isCurrent 
                  ? '#FFFFFF' 
                  : (isLightMode ? '#334155' : 'hsl(220, 18%, 82%)'),
                fontSize: isMobile ? '0.72rem' : '0.8rem',
                background: isCurrent 
                  ? `linear-gradient(135deg, ${item.color} 0%, rgba(15, 23, 42, 0.95) 100%)`
                  : (isLightMode 
                      ? 'linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%)' 
                      : 'linear-gradient(135deg, rgba(15, 23, 42, 0.92) 0%, rgba(30, 41, 59, 0.85) 100%)'),
                border: isCurrent 
                  ? `1px solid ${item.color}` 
                  : (isLightMode ? `1px solid rgba(0, 122, 255, 0.15)` : `1px solid rgba(255,255,255,0.08)`),
                backdropFilter: 'blur(10px)',
                boxShadow: isCurrent 
                  ? `0 4px 15px rgba(0,0,0,0.3), 0 0 10px ${item.color}33`
                  : (isLightMode ? `0 4px 12px rgba(0,0,0,0.03)` : `0 4px 12px rgba(0,0,0,0.15)`),
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                minHeight: '48px',
              }}
            >
              <span style={{ color: isCurrent ? '#fff' : item.color, display: 'inline-flex' }}>{item.icon}</span>
              <span style={{ whiteSpace: 'nowrap', fontWeight: isCurrent ? 700 : 500 }}>{item.label}</span>
            </button>
          );
        })}
      </div>
 
      {/* Zoom controls */}
      <div
        style={{
          position: 'absolute',
          right: isMobile ? '12px' : '20px',
          bottom: isMobile ? '130px' : (isTablet ? '20px' : 'auto'),
          top: isMobile ? 'auto' : (isTablet ? 'auto' : '20px'),
          zIndex: 40,
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <button
          onClick={handleZoomIn}
          aria-label="Acercar mapa"
          style={{
            background: isLightMode ? '#ffffff' : 'rgba(15, 23, 42, 0.85)',
            border: isLightMode ? '1px solid rgba(0, 122, 255, 0.25)' : '1px solid rgba(0, 122, 255, 0.4)',
            color: isLightMode ? '#007AFF' : '#fff',
            borderRadius: '10px',
            cursor: 'pointer',
            backdropFilter: 'blur(12px)',
            boxShadow: isLightMode ? '0 4px 12px rgba(0,0,0,0.05)' : '0 4px 15px rgba(0,0,0,0.3)',
            transition: 'all 0.2s ease',
            width: '48px',
            height: '48px',
            minWidth: '48px',
            minHeight: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onMouseEnter={(e) => e.currentTarget.style.borderColor = '#007AFF'}
          onMouseLeave={(e) => e.currentTarget.style.borderColor = isLightMode ? 'rgba(0, 122, 255, 0.25)' : 'rgba(0, 122, 255, 0.4)'}
        >
          <Plus size={20} />
        </button>
        <button
          onClick={handleZoomOut}
          aria-label="Alejar mapa"
          style={{
            background: isLightMode ? '#ffffff' : 'rgba(15, 23, 42, 0.85)',
            border: isLightMode ? '1px solid rgba(0, 122, 255, 0.25)' : '1px solid rgba(0, 122, 255, 0.4)',
            color: isLightMode ? '#007AFF' : '#fff',
            borderRadius: '10px',
            cursor: 'pointer',
            backdropFilter: 'blur(12px)',
            boxShadow: isLightMode ? '0 4px 12px rgba(0,0,0,0.05)' : '0 4px 15px rgba(0,0,0,0.3)',
            transition: 'all 0.2s ease',
            width: '48px',
            height: '48px',
            minWidth: '48px',
            minHeight: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onMouseEnter={(e) => e.currentTarget.style.borderColor = '#007AFF'}
          onMouseLeave={(e) => e.currentTarget.style.borderColor = isLightMode ? 'rgba(0, 122, 255, 0.25)' : 'rgba(0, 122, 255, 0.4)'}
        >
          <Minus size={20} />
        </button>
        <button
          onClick={handleReset}
          aria-label="Restablecer mapa"
          style={{
            background: isLightMode ? '#ffffff' : 'rgba(15, 23, 42, 0.85)',
            border: isLightMode ? '1px solid rgba(0, 122, 255, 0.25)' : '1px solid rgba(0, 122, 255, 0.4)',
            color: isLightMode ? '#007AFF' : '#fff',
            borderRadius: '10px',
            cursor: 'pointer',
            backdropFilter: 'blur(12px)',
            boxShadow: isLightMode ? '0 4px 12px rgba(0,0,0,0.05)' : '0 4px 15px rgba(0,0,0,0.3)',
            transition: 'all 0.2s ease',
            width: '48px',
            height: '48px',
            minWidth: '48px',
            minHeight: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onMouseEnter={(e) => e.currentTarget.style.borderColor = '#007AFF'}
          onMouseLeave={(e) => e.currentTarget.style.borderColor = isLightMode ? 'rgba(0, 122, 255, 0.25)' : 'rgba(0, 122, 255, 0.4)'}
        >
          <Maximize size={20} />
        </button>
      </div>

      {/* Map Implementation */}
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{ scale: isMobile ? 2200 : 2600 }}
        style={{ width: '100%', height: '100%', outline: 'none' }}
      >
        <ZoomableGroup
          key={isMobile ? 'mobile' : 'desktop'}
          zoom={position.zoom}
          center={position.coordinates}
          onMoveEnd={(pos) => setPosition(pos)}
        >
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const name = geo.properties.name;
                const allowed = ['Venezuela', 'Guyana', 'Colombia', 'Brazil', 'Trinidad and Tobago'];
                if (!allowed.includes(name)) return null;

                const isVenezuela = name === 'Venezuela';
                const isGuyana = name === 'Guyana';

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={
                      isLightMode
                        ? (isVenezuela ? 'rgba(0, 122, 255, 0.08)' : '#f1f5f9')
                        : (isVenezuela ? 'rgba(0, 122, 255, 0.04)' : 'rgba(0, 0, 0, 0.5)')
                    }
                    stroke={
                      isLightMode
                        ? (isVenezuela ? '#007AFF' : 'rgba(0, 122, 255, 0.25)')
                        : (isVenezuela ? '#007AFF' : 'rgba(0,122,255,0.15)')
                    }
                    strokeWidth={isVenezuela ? 1.25 : 0.4}
                    style={{
                      default: { outline: 'none' },
                      hover: {
                        outline: 'none',
                        fill: isVenezuela 
                          ? (isLightMode ? 'rgba(0,122,255,0.18)' : 'rgba(0,122,255,0.08)')
                          : (isLightMode ? '#e2e8f0' : 'rgba(255,255,255,0.03)'),
                      },
                      pressed: { outline: 'none' },
                    }}
                  />
                );
              })
            }
          </Geographies>

          {/* Network Lines */}
          {filteredLocations.map((loc, index) => {
            if (loc.id === 'sede') return null;
            const isActive = index === activeIndex;
            const color = TYPE_META[loc.type].color;

            return (
              <Line
                key={`line-${loc.id}`}
                from={ORIGIN}
                to={loc.coordinates}
                stroke={isActive ? color : 'rgba(0,122,255,0.16)'}
                strokeWidth={isActive ? 1.8 : 0.8}
                strokeDasharray={isActive ? 'none' : '4 5'}
                strokeLinecap="round"
                opacity={loc.type === 'faja' ? 0.45 : 0.7}
              />
            );
          })}

          {/* Location Markers */}
          {filteredLocations.map((loc, index) => {
            const isActive = index === activeIndex;

            return (
              <Marker key={loc.id} coordinates={loc.coordinates}>
                <motion.g
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{
                    delay: 0.1 + index * 0.02,
                    type: 'spring',
                    stiffness: 240,
                    damping: 18,
                  }}
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    setIsAutoRotating(false);
                    setActiveIndex(index);
                    setPosition({
                      coordinates: loc.coordinates,
                      zoom: loc.type === 'faja' ? 2.1 : 2.5,
                    });
                  }}
                >
                  {renderMarkerShape(loc, isActive)}
                </motion.g>
              </Marker>
            );
          })}
        </ZoomableGroup>
      </ComposableMap>

      {/* Floating Info Tooltip */}
      <AnimatePresence mode="wait">
        {activeLocation && (
          <motion.div
            key={activeLocation.id}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            style={{
              position: 'absolute',
              bottom: isMobile ? '12px' : '30px',
              left: isMobile ? '12px' : '50%',
              right: isMobile ? '12px' : 'auto',
              transform: isMobile ? 'none' : 'translateX(-50%)',
              background: isLightMode 
                ? 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                : 'linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.95) 100%)',
              backdropFilter: 'blur(16px)',
              padding: isMobile ? '12px 16px' : '18px 24px',
              borderRadius: '20px',
              border: isLightMode 
                ? `1px solid ${TYPE_META[activeLocation.type].color}88`
                : `1px solid ${TYPE_META[activeLocation.type].color}55`,
              boxShadow: isLightMode 
                ? `0 15px 35px rgba(0,122,255,0.06), 0 0 20px ${TYPE_META[activeLocation.type].glow}33`
                : `0 15px 35px rgba(0,0,0,0.6), 0 0 20px ${TYPE_META[activeLocation.type].glow}`,
              color: isLightMode ? 'var(--text-primary)' : '#fff',
              textAlign: 'center',
              width: isMobile ? 'auto' : 'max-content',
              maxWidth: isMobile ? 'none' : '400px',
              pointerEvents: 'none',
              zIndex: 50,
            }}
          >
            <div
              style={{
                marginBottom: '6px',
                fontSize: '0.75rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: TYPE_META[activeLocation.type].color,
                fontWeight: 700,
              }}
            >
              {TYPE_META[activeLocation.type].label}
            </div>

            <h3
              style={{
                margin: '0 0 6px 0',
                color: isLightMode ? '#0f172a' : '#F8FAFC',
                fontFamily: 'Space Grotesk, sans-serif',
                fontSize: '1.05rem',
                lineHeight: 1.2,
              }}
            >
              {activeLocation.title}
            </h3>

            <p
              style={{
                margin: 0,
                fontSize: '0.88rem',
                color: isLightMode ? '#475569' : 'hsl(220, 18%, 82%)',
              }}
            >
              {activeLocation.subtitle}
            </p>

            {(activeLocation.type === 'division' || activeLocation.type === 'faja') && (
              <div
                style={{
                  marginTop: '12px',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.25)',
                  fontSize: '0.74rem',
                  color: '#EF4444',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  justifyContent: 'center',
                  lineHeight: '1.25'
                }}
              >
                <span className="pulse-glow" style={{ 
                  width: '6px', 
                  height: '6px', 
                  borderRadius: '50%', 
                  background: '#EF4444', 
                  display: 'inline-block',
                  flexShrink: 0
                }} />
                <span>Respuesta Crítica: Movilización y disponibilidad de ingeniería certificada en 72 horas.</span>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const rootElement = document.getElementById('interactive-map-root');
if (rootElement) {
  const root = createRoot(rootElement);
  root.render(<InteractiveMap />);
}
